import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Trash2, RotateCcw, Send, ChevronRight, Wallet } from 'lucide-react';
import { supabase } from '../supabaseClient';
import { useSession } from '../lib/SessionContext';
import { t } from '../i18n';
import { ENTITY, removeEntry, reactivateEntry, requestRemoval } from '../lib/masterData';
import {
  Card, Button, Input, Field, Badge, Alert, Spinner, EmptyState, Modal,
} from '../components/ui';
import RemovalRequests from '../components/RemovalRequests';

/**
 * Coverages and disciplines: the lists the whole practice shares. What each
 * professional charges under them is in Configuración → Cobros.
 *
 * The governance rule runs throughout: professionals add, only admin removes,
 * and a professional who wants something gone files a request, which admin
 * answers in components/RemovalRequests.js.
 */

const TwoLevelList = ({
  parents, childrenByParent, selectedParent, onSelectParent,
  onAddParent, onAddChild, onRemove, onReactivate, onRequestRemoval,
  isAdmin, labels, busy,
}) => {
  const [newParent, setNewParent] = useState('');
  const [newChild, setNewChild] = useState('');

  const children = selectedParent ? (childrenByParent[selectedParent.id] ?? []) : [];

  return (
    <div className="grid gap-6 lg:grid-cols-2">
      <div>
        <h3 className="mb-3 text-sm font-semibold uppercase tracking-wide text-slate-500">
          {labels.parentPlural}
        </h3>
        <div className="mb-3 flex gap-2">
          <Input
            value={newParent}
            onChange={(event) => setNewParent(event.target.value)}
            placeholder={labels.addParent}
            onKeyDown={(event) => {
              if (event.key === 'Enter' && newParent.trim()) {
                onAddParent(newParent.trim());
                setNewParent('');
              }
            }}
          />
          <Button
            disabled={!newParent.trim() || busy}
            onClick={() => { onAddParent(newParent.trim()); setNewParent(''); }}
          >
            <Plus size={16} />
          </Button>
        </div>

        <ul className="divide-y divide-slate-100 rounded-lg border border-slate-200">
          {parents.map((parent) => (
            <li
              key={parent.id}
              className={`flex items-center justify-between gap-2 px-3 py-2 ${
                selectedParent?.id === parent.id ? 'bg-indigo-50' : ''
              }`}
            >
              <button
                onClick={() => onSelectParent(parent)}
                className="flex flex-1 items-center gap-2 text-left text-sm"
              >
                <span className={parent.is_active ? 'text-slate-900' : 'text-slate-400 line-through'}>
                  {parent.name}
                </span>
                {!parent.is_active && <Badge tone="neutral">{t('masterData.inactive')}</Badge>}
                <ChevronRight size={14} className="ml-auto text-slate-300" />
              </button>
              {isAdmin ? (
                parent.is_active ? (
                  <button
                    onClick={() => onRemove(labels.parentEntity, parent)}
                    className="rounded p-1 text-slate-400 hover:bg-red-50 hover:text-red-600"
                    title={t('common.delete')}
                  >
                    <Trash2 size={14} />
                  </button>
                ) : (
                  <button
                    onClick={() => onReactivate(labels.parentEntity, parent)}
                    className="rounded p-1 text-slate-400 hover:bg-emerald-50 hover:text-emerald-600"
                    title={t('masterData.reactivate')}
                  >
                    <RotateCcw size={14} />
                  </button>
                )
              ) : (
                <button
                  onClick={() => onRequestRemoval(labels.parentEntity, parent)}
                  className="rounded p-1 text-slate-400 hover:bg-amber-50 hover:text-amber-600"
                  title={t('masterData.requestRemoval')}
                >
                  <Send size={14} />
                </button>
              )}
            </li>
          ))}
        </ul>
      </div>

      <div>
        <h3 className="mb-3 text-sm font-semibold uppercase tracking-wide text-slate-500">
          {selectedParent ? `${labels.childPlural} · ${selectedParent.name}` : labels.childPlural}
        </h3>

        {!selectedParent ? (
          <EmptyState title={labels.selectParentFirst} />
        ) : (
          <>
            <div className="mb-3 flex gap-2">
              <Input
                value={newChild}
                onChange={(event) => setNewChild(event.target.value)}
                placeholder={labels.addChild}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' && newChild.trim()) {
                    onAddChild(selectedParent.id, newChild.trim());
                    setNewChild('');
                  }
                }}
              />
              <Button
                disabled={!newChild.trim() || busy}
                onClick={() => { onAddChild(selectedParent.id, newChild.trim()); setNewChild(''); }}
              >
                <Plus size={16} />
              </Button>
            </div>

            {children.length === 0 ? (
              <EmptyState title={labels.noChildren} />
            ) : (
              <ul className="divide-y divide-slate-100 rounded-lg border border-slate-200">
                {children.map((child) => (
                  <li key={child.id} className="flex items-center justify-between gap-2 px-3 py-2">
                    <span className={`text-sm ${child.is_active ? 'text-slate-900' : 'text-slate-400 line-through'}`}>
                      {child.name}
                    </span>
                    <div className="flex items-center gap-1">
                      {!child.is_active && <Badge tone="neutral">{t('masterData.inactive')}</Badge>}
                      {isAdmin ? (
                        child.is_active ? (
                          <button
                            onClick={() => onRemove(labels.childEntity, child)}
                            className="rounded p-1 text-slate-400 hover:bg-red-50 hover:text-red-600"
                          >
                            <Trash2 size={14} />
                          </button>
                        ) : (
                          <button
                            onClick={() => onReactivate(labels.childEntity, child)}
                            className="rounded p-1 text-slate-400 hover:bg-emerald-50 hover:text-emerald-600"
                          >
                            <RotateCcw size={14} />
                          </button>
                        )
                      ) : (
                        <button
                          onClick={() => onRequestRemoval(labels.childEntity, child)}
                          className="rounded p-1 text-slate-400 hover:bg-amber-50 hover:text-amber-600"
                        >
                          <Send size={14} />
                        </button>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </>
        )}
      </div>
    </div>
  );
};

const MasterDataView = () => {
  const { isAdmin, therapistId } = useSession();
  const navigate = useNavigate();
  const [tab, setTab] = useState('coverages');
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState(null);

  const [companies, setCompanies] = useState([]);
  const [plansByCompany, setPlansByCompany] = useState({});
  const [selectedCompany, setSelectedCompany] = useState(null);

  const [disciplines, setDisciplines] = useState([]);
  const [specsByDiscipline, setSpecsByDiscipline] = useState({});
  const [selectedDiscipline, setSelectedDiscipline] = useState(null);

  const [removalTarget, setRemovalTarget] = useState(null);
  const [removalReason, setRemovalReason] = useState('');
  const [pendingCount, setPendingCount] = useState(0);

  const groupBy = (rows, key) =>
    (rows ?? []).reduce((acc, row) => {
      (acc[row[key]] ??= []).push(row);
      return acc;
    }, {});

  const load = useCallback(async () => {
    setLoading(true);
    const [companyRows, planRows, disciplineRows, specRows, requests] = await Promise.all([
      supabase.from('insurance_companies').select('*').order('name'),
      supabase.from('insurance_plans').select('*').order('name'),
      supabase.from('disciplines').select('*').order('name'),
      supabase.from('specializations').select('*').order('name'),
      supabase.from('master_data_removal_requests')
        .select('id', { count: 'exact', head: true }).eq('status', 'pending'),
    ]);
    setCompanies(companyRows.data ?? []);
    setPlansByCompany(groupBy(planRows.data, 'company_id'));
    setDisciplines(disciplineRows.data ?? []);
    setSpecsByDiscipline(groupBy(specRows.data, 'discipline_id'));
    setPendingCount(requests.count ?? 0);
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const add = async (table, payload) => {
    setBusy(true);
    const { error } = await supabase.from(table).insert([{ ...payload, created_by_therapist_id: therapistId }]);
    setBusy(false);
    if (error) {
      setNotice({
        tone: 'danger',
        text: error.code === '23505' ? t('masterData.duplicate') : error.message,
      });
      return;
    }
    setNotice(null);
    load();
  };

  const handleRemove = async (entityType, entry) => {
    if (!window.confirm(t('masterData.confirmRemove', { name: entry.name }))) return;
    setBusy(true);
    const { mode, usage, error } = await removeEntry(entityType, entry.id);
    setBusy(false);
    if (error) { setNotice({ tone: 'danger', text: error.message }); return; }
    setNotice({
      tone: 'success',
      text: mode === 'deleted'
        ? t('masterData.removed', { name: entry.name })
        : t('masterData.deactivated', { name: entry.name, count: usage }),
    });
    load();
  };

  const handleReactivate = async (entityType, entry) => {
    await reactivateEntry(entityType, entry.id);
    load();
  };

  const submitRemovalRequest = async () => {
    setBusy(true);
    const { error } = await requestRemoval({
      therapistId,
      entityType: removalTarget.entityType,
      entityId: removalTarget.entry.id,
      reason: removalReason,
    });
    setBusy(false);
    setRemovalTarget(null);
    setRemovalReason('');
    if (error) { setNotice({ tone: 'danger', text: error.message }); return; }
    setNotice({ tone: 'success', text: t('masterData.requestSent') });
    load();
  };

  const tabs = [
    { id: 'coverages', label: t('masterData.tabCoverages') },
    { id: 'disciplines', label: t('masterData.tabDisciplines') },
    ...(isAdmin ? [{ id: 'requests', label: t('masterData.tabRequests'), badge: pendingCount }] : []),
  ];

  if (loading) return <Spinner />;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">{t('nav.masterData')}</h1>
          <p className="text-sm text-slate-500">
            {isAdmin ? t('masterData.subtitleAdmin') : t('masterData.subtitleTherapist')}
          </p>
        </div>
        {/* What each professional charges was a tab here until it moved. */}
        <Button variant="secondary" onClick={() => navigate('/configuracion/cobros')}>
          <Wallet size={16} />{t('masterData.goToCharges')}
        </Button>
      </div>

      <div className="flex flex-wrap gap-1 border-b border-slate-200">
        {tabs.map((item) => (
          <button
            key={item.id}
            onClick={() => setTab(item.id)}
            className={`flex items-center gap-2 border-b-2 px-4 py-2 text-sm font-medium transition ${
              tab === item.id
                ? 'border-indigo-600 text-indigo-700'
                : 'border-transparent text-slate-500 hover:text-slate-700'
            }`}
          >
            {item.label}
            {item.badge > 0 && <Badge tone="warning">{item.badge}</Badge>}
          </button>
        ))}
      </div>

      {notice && <Alert tone={notice.tone}>{notice.text}</Alert>}

      <Card>
        {tab === 'coverages' && (
          <TwoLevelList
            parents={companies}
            childrenByParent={plansByCompany}
            selectedParent={selectedCompany}
            onSelectParent={setSelectedCompany}
            onAddParent={(name) => add('insurance_companies', { name })}
            onAddChild={(companyId, name) => add('insurance_plans', { company_id: companyId, name })}
            onRemove={handleRemove}
            onReactivate={handleReactivate}
            onRequestRemoval={(entityType, entry) => setRemovalTarget({ entityType, entry })}
            isAdmin={isAdmin}
            busy={busy}
            labels={{
              parentPlural: t('insurance.companyPlural'),
              childPlural: t('insurance.planPlural'),
              addParent: t('insurance.addCompany'),
              addChild: t('insurance.addPlan'),
              selectParentFirst: t('masterData.selectCompanyFirst'),
              noChildren: t('masterData.noPlans'),
              parentEntity: ENTITY.COMPANY,
              childEntity: ENTITY.PLAN,
            }}
          />
        )}

        {tab === 'disciplines' && (
          <TwoLevelList
            parents={disciplines}
            childrenByParent={specsByDiscipline}
            selectedParent={selectedDiscipline}
            onSelectParent={setSelectedDiscipline}
            onAddParent={(name) => add('disciplines', { name })}
            onAddChild={(disciplineId, name) => add('specializations', { discipline_id: disciplineId, name })}
            onRemove={handleRemove}
            onReactivate={handleReactivate}
            onRequestRemoval={(entityType, entry) => setRemovalTarget({ entityType, entry })}
            isAdmin={isAdmin}
            busy={busy}
            labels={{
              parentPlural: t('specializations.disciplinePlural'),
              childPlural: t('specializations.specializationPlural'),
              addParent: t('specializations.addDiscipline'),
              addChild: t('specializations.addSpecialization'),
              selectParentFirst: t('masterData.selectDisciplineFirst'),
              noChildren: t('masterData.noSpecializations'),
              parentEntity: ENTITY.DISCIPLINE,
              childEntity: ENTITY.SPECIALIZATION,
            }}
          />
        )}

        {tab === 'requests' && isAdmin && (
          <RemovalRequests onResolved={load} />
        )}
      </Card>

      <Modal
        open={Boolean(removalTarget)}
        title={t('masterData.requestRemoval')}
        onClose={() => setRemovalTarget(null)}
        footer={
          <>
            <Button variant="secondary" onClick={() => setRemovalTarget(null)}>{t('common.cancel')}</Button>
            <Button onClick={submitRemovalRequest} loading={busy}>{t('masterData.sendRequest')}</Button>
          </>
        }
      >
        <p className="mb-3 text-sm text-slate-600">
          {t('masterData.requestRemovalBody', { name: removalTarget?.entry?.name ?? '' })}
        </p>
        <Field label={t('masterData.reason')}>
          <Input
            value={removalReason}
            onChange={(event) => setRemovalReason(event.target.value)}
            placeholder={t('masterData.reasonPlaceholder')}
          />
        </Field>
      </Modal>
    </div>
  );
};

export default MasterDataView;
